import {
  StateCreator, UseBoundStore, StoreApi, create,
} from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  Render, RenderStyle, HistoryPlugins, MouseCreate,
} from '@meitu/whee-infinite-canvas';
import useChatStore from '$/store/useChatStore';
import { suitableLoaction } from './useCanvasObjectStore';

type RenderedStatus = 'pending' | 'success' | 'failed';

interface TCanvasState {
  render: Render | null;
  renderedStatus: RenderedStatus;
  mouseCreate: MouseCreate | null;
  historyPlugins: HistoryPlugins | null;
  renderStyle: RenderStyle | null;
  zoomNum: number;
}

const canvasSlice: StateCreator<TCanvasState, [
  ['zustand/immer', never],
]> = () => ({
  render: null,
  renderStyle: null,
  mouseCreate: null,
  historyPlugins: null,
  zoomNum: 25,
  renderedStatus: 'pending',
});

// @ts-ignore
const useCanvasStore: UseBoundStore<StoreApi<TCanvasState>> = create<TCanvasState>()(immer(canvasSlice));

export default useCanvasStore;

/**
 * 新建画布实例
 *
 * @export
 * @param {Render} render
 */
export function setRender(render: Render) {
  useCanvasStore.setState(() => ({ render }));
}

/**
 * 渲染完成状态
 *
 * @export
 */
export function setRenderedStatus(status: 'success' | 'failed') {
  useCanvasStore.setState(() => ({ renderedStatus: status }));
}

/**
 * 订阅渲染完成状态
 *
 * @export
 * @return {*}
 */
export function subscribeRenderedStatus() {
  return new Promise((resolve, reject) => {
    if (useCanvasStore.getState().renderedStatus === 'success') {
      resolve(true);
      return;
    }
    if (useCanvasStore.getState().renderedStatus === 'failed') {
      reject();
      return;
    }
    useCanvasStore.subscribe((newState, oldState) => {
      if (oldState.renderedStatus === 'pending' && newState.renderedStatus === 'success') {
        resolve(true);
        return;
      }
      if (oldState.renderedStatus === 'pending' && newState.renderedStatus === 'failed') {
        reject();
      }
    });
  });
}

export function setMouseCreate(mouseCreate: MouseCreate) {
  useCanvasStore.setState(() => ({ mouseCreate }));
}

export function setRenderStyle(renderStyle: RenderStyle) {
  useCanvasStore.setState(() => ({ renderStyle }));
}

/**
 * 初始化历史记录插件
 * @export
 * @param {HistoryPlugins} plugin
 */
export function setHistoryPlugins(plugin: HistoryPlugins) {
  useCanvasStore.setState(() => ({ historyPlugins: plugin }));
}

// 设置zoom
export const setZoomNum = (val: number) => {
  useCanvasStore.setState(() => ({ zoomNum: val }));
};

export const handleZoom = (value: number) => {
  const { render, zoomNum } = useCanvasStore.getState();
  const currentValue = Math.max(Math.min(zoomNum + value, 400), 1);
  render?._FC.fire('viewport:zoom', { zoom: currentValue / 100 });
  render?.setZoom(currentValue / 100);
};

/**
 * 适应画布Zoom to fit
 *
 * @export
 * @return {*}
 */
export function handleShowAll() {
  const { render } = useCanvasStore.getState();
  if (render?._FC.getObjects().length === 0) return;
  const { sideBarCollapsed, sideBarWidth } = useChatStore.getState();
  const sideBarLeft = sideBarCollapsed ? sideBarWidth : 0;
  render?.zoomToFitCanvas(0.01, sideBarLeft);
}

/**
 * 适应选中对象Zoom to selection
 *
 */
export function SelectBySuitableLoaction() {
  const { render } = useCanvasStore.getState();
  const activeObjects = render?._FC.getActiveObjects();
  if (!activeObjects) return;
  suitableLoaction(activeObjects);
}
